// src/lib/purchaseOrderSend.js
// Session 78n8.2, Issue #395 (+ #257). Gate Screen 2, decisions D3 and D4.
//
// WHAT THIS DOES, in plain words: once Jennifer has confirmed the order, this is
// what actually reaches people. The vendor gets the purchase order, and the
// person who asked for the material gets told it was ordered. Both in one
// action, which is the requirement -- there is no second screen to forget.
//
// IT NEVER THROWS, and that is the most important thing about it. By the time
// this runs the order is ALREADY RECORDED: the request row is updated, the
// prices are written, the status is `ordered`. Throwing here would surface as a
// red error on a sheet whose work succeeded, and the obvious response to that
// error is to press Confirm again -- which is how a second split request gets
// created, the exact failure #350's mitigation exists to prevent.
//
// So every path returns a result the caller can SHOW. "The order is placed but
// the email did not go" is the honest sentence, and it is far better than
// either a false success or an error implying nothing happened. Same rule #351
// established for the vendor price write.

import { renderPurchaseOrderHtml, purchaseOrderSubject, buildMailtoLink } from './purchaseOrder';
import { renderPurchaseOrderPdf } from './purchaseOrderPdf';
import { sendEmail, sendNotification, getRequestApproverIds } from './notify';
import { supabase } from './supabase';

// The requester's confirmation. Deliberately NOT the same email the branch
// managers get from notifyRequestOrdered(): that one reports an event to people
// supervising a branch, and this one answers the question the requester has been
// carrying since they asked -- is my material coming, and when.
function requesterConfirmationHtml({ order, requestNumber, buyerName }) {
  const eta = order.lines.length;
  return `
<div style="font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;color:#1A1A1A;line-height:1.6;">
  <p>Your request <strong>${requestNumber}</strong> has been ordered.</p>
  <p>
    ${buyerName ? buyerName + ' placed it' : 'It was placed'} with
    <strong>${order.vendor.name}</strong>${order.poRef ? ` under PO <strong>${order.poRef}</strong>` : ''}
    — ${eta} line${eta === 1 ? '' : 's'}.
  </p>
  <p>You will be notified again when it is received.</p>
</div>`;
}

export async function deliverPurchaseOrder({
  order,
  mode,
  sendPo,
  sendConfirmation,
  requesterId,
  requestId,
  requestNumber,
  buyer,
  // #413, Session 78n8.3 -- who the in-app approver notification goes to. Same
  // two values getRequestApproverEmails() takes in notifyRequestOrdered(), so
  // the bell and the email resolve to the same people by construction.
  branchId,
  departmentId,
  // Lower-cased addresses notifyRequestOrdered() has already mailed, so the
  // requester is not emailed twice about one order when they are also a
  // manager at that branch. Defaults to none, which is the safe direction:
  // a caller that forgets it sends one email too many, never one too few.
  managerEmails = [],
}) {
  const notes = [];
  let ok = true;
  let mailtoHref = null;

  // ---- the frozen copy of the document -------------------------------------
  //
  // #413. FIRST, before anything is sent, and deliberately so: if the vendor
  // send fails, the order has still been placed and the document still needs to
  // exist. Doing this last would mean the one case where somebody most wants to
  // see what was built is the one case where nothing was kept.
  //
  // Written on EVERY route, including "Don't send" -- a phone order still has a
  // purchase order behind it, AP still receives a bill against it, and #287
  // still issued it a PO number. The document is a record of what was ordered,
  // not a record of how it was transmitted.
  //
  // The snapshot is the MODEL, not the rendered PDF. renderPurchaseOrderPdf()
  // draws from exactly this, so a re-download runs the identical generator over
  // the identical input. See the migration header for why a re-derivation from
  // live rows cannot work: neither the buyer's name nor this note is stored
  // anywhere else, and prices, addresses and vendor records all drift.
  //
  // Failure is NOTED AND NOT FATAL, same rule as everything else in this file:
  // by the time this runs the order is already recorded, and throwing would
  // surface a red error on a sheet whose work succeeded -- which invites
  // pressing Confirm again and creating a second order (#350).
  if (requestId) {
    try {
      const { error } = await supabase
        .from('material_requests')
        .update({ po_document: order })
        .eq('id', requestId);
      if (error) throw error;
    } catch (e) {
      console.error('[#413] purchase-order snapshot failed', e);
      notes.push('the order was placed, but a copy of the purchase order could not be saved for later');
    }
  }

  // ---- the vendor's copy --------------------------------------------------
  if (mode === 'send' && sendPo) {
    try {
      // The PDF is built BEFORE the send rather than inside it, so a failure to
      // draw the document is distinguishable from a failure to deliver it. Those
      // are different problems with different fixes and the buyer deserves to be
      // told which one happened.
      let attachments;
      try {
        const { base64, filename } = await renderPurchaseOrderPdf(order);
        attachments = [{ filename, content: base64 }];
      } catch (pdfErr) {
        console.error('[#395] PDF build failed; sending without the attachment', pdfErr);
        notes.push('the PDF could not be built, so the order was sent without it');
      }

      const result = await sendEmail(
        order.vendor.email,
        purchaseOrderSubject(order),
        renderPurchaseOrderHtml(order),
        {
          // D4. Replies reach the buyer rather than a mailbox nobody reads, and
          // she is bcc'd so the sent order is in her own mail too.
          replyTo: buyer?.email,
          bcc: buyer?.email,
          attachments,
        }
      );

      if (result?.suppressed) {
        notes.push(`the order was NOT emailed — sending is disabled outside production (it would have gone to ${order.vendor.email})`);
      } else if (!result?.ok) {
        ok = false;
        notes.push(`the order could not be emailed to ${order.vendor.email}: ${result?.error || 'unknown error'}`);
      } else {
        notes.push(`the order was emailed to ${order.vendor.email}${attachments ? ' with the PDF attached' : ''}`);
      }
    } catch (e) {
      ok = false;
      console.error('[#395] vendor send failed', e);
      notes.push(`the order could not be emailed: ${e?.message || 'unknown error'}`);
    }
  }

  // ---- the mail-client draft ----------------------------------------------
  if (mode === 'draft') {
    const link = buildMailtoLink(order);
    mailtoHref = link.href;
    // Handed back rather than opened from in here. A mailto: opened after an
    // await can be swallowed by the browser as a non-gesture navigation, and
    // when that happens NOTHING VISIBLE HAPPENS -- which is the same silent
    // failure F4 describes for Outlook's length ceiling. The caller shows the
    // link as well as trying to open it, so there is always something to click.
    notes.push('your mail app should open with the order ready to send');
    if (link.tooLongForOutlook) {
      notes.push('this order is long, so Outlook may refuse to open the draft — use the link if nothing appears');
    }
  }

  // ---- the requester's confirmation ---------------------------------------
  if (sendConfirmation && requesterId) {
    try {
      // In-app first, because it is the one that cannot bounce.
      //
      // Worth knowing: before this, a requester was told nothing when their
      // material was actually ordered. The only sendNotification() aimed at them
      // on this path fires when an order goes OVER the approval threshold. The
      // ordinary case -- the order simply being placed -- reached branch
      // managers by email and the requester not at all.
      await sendNotification(
        [requesterId],
        'Your request has been ordered',
        `${requestNumber} — ordered from ${order.vendor.name}${order.poRef ? ` under PO ${order.poRef}` : ''}.`,
        // material_request_ordered, domain 'inventory' -- added by
        // db/migrations/20260830_notifications_material_request_ordered.sql.
        // NOT 'request_ordered': that prefix is the TOOLS-domain spare-request
        // family, and the collision has already caused real confusion once
        // (#161). This sits with material_request_approved/denied, the same
        // lifecycle seen by the same person.
        'material_request_ordered', 'inventory', 'my_requests', requestId
      );

      // COLBY'S RULE, Session 78n8.2, and it overrides the gate's "in-app and
      // email" for one of the two readers: *"if it was a team member who made
      // the request the team member should never receive email. they will
      // likely be using personal emails and i dont want to fill up their
      // inboxes with work emails."*
      //
      // So the email half is gated on ROLE, not on who the requester is. A
      // team member gets the in-app notification and the toast and nothing in
      // their inbox; a manager, executive, admin or purchasing user -- people
      // with work addresses -- still gets the email.
      //
      // The role is read here rather than passed in because the REQUESTER's
      // role is what decides this, and the caller only knows the BUYER's.
      const { data: requester } = await supabase
        .from('users').select('email, role').eq('id', requesterId).maybeSingle();

      const isTeamMember = requester?.role === 'team_member';

      // DOUBLE-NOTIFICATION GUARD, also Colby's, and it is a real case rather
      // than a hypothetical: *"when it's an approving manager who makes the
      // original material request and also approves it then we dont want to
      // send double notifications to that manager."*
      //
      // notifyRequestOrdered() has already emailed the branch managers by the
      // time this runs. If the requester IS one of them, a second email here
      // is the same event arriving twice, minutes apart, about the same order.
      // Suppressed rather than deduplicated downstream, because the two mails
      // are composed by different functions and only this one knows both fired.
      const alreadyEmailedAsManager = managerEmails.includes((requester?.email || '').toLowerCase());

      if (requester?.email && !isTeamMember && !alreadyEmailedAsManager) {
        await sendEmail(
          requester.email,
          `Ordered — ${requestNumber}`,
          requesterConfirmationHtml({ order, requestNumber, buyerName: buyer?.full_name })
        );
        notes.push('the requester was told it is on order, in-app and by email');
      } else if (isTeamMember) {
        notes.push('the requester was told it is on order, in-app only');
      } else if (alreadyEmailedAsManager) {
        notes.push('the requester was told it is on order, in-app only — they already had the manager email');
      } else {
        notes.push('the requester was told it is on order');
      }
    } catch (e) {
      // Deliberately NOT failing the whole result. The vendor has the order;
      // an internal confirmation that did not send is a smaller problem, and
      // reporting it as a failure would imply the vendor never got it.
      console.error('[#395] requester confirmation failed', e);
      notes.push('the requester could not be notified');
    }
  }

  // ---- the approvers' in-app notification ---------------------------------
  //
  // #413, Session 78n8.3. The managers who approved this spend were told by
  // email and never in the bell, so the one surface they actually live in said
  // nothing when their approved money was committed.
  //
  // AUDIENCE IS THE APPROVERS, NOT EVERY BRANCH MANAGER -- Colby's decision,
  // overturning the recommendation. *"a non approving manager doesnt need
  // notifications they cant act on. thats pointless and will make someone start
  // ignoring their notifications."* Same set as the email above it, by
  // construction rather than by coincidence: both resolve through the #396
  // approver lookup from the same branchId and departmentId.
  //
  // THE REQUESTER IS REMOVED FROM THE LIST, and this is Colby's own
  // double-notification case rather than a hypothetical -- *"when it's an
  // approving manager who makes the original material request and also approves
  // it then we dont want to send double notifications to that manager."* Such a
  // person has already had `material_request_ordered` a few lines above, as the
  // requester. Without this they get two bells, seconds apart, about one order.
  if (branchId) {
    try {
      const approverIds = (await getRequestApproverIds(branchId, departmentId))
        .filter(id => id !== requesterId);

      if (approverIds.length) {
        await sendNotification(
          approverIds,
          'Purchase order issued',
          `${requestNumber} — ordered from ${order.vendor.name}${order.poRef ? ` under PO ${order.poRef}` : ''}.`,
          // material_request_po_issued, domain 'inventory' -- added by
          // db/migrations/20260830_po_document_and_approver_notification.sql.
          // NOT material_request_ordered: that one is the REQUESTER'S and means
          // "your material is on its way". Reusing it for a different reader
          // with a different destination is Finding 5 in
          // NOTIFICATION_VOCABULARY.md, logged as a defect and resolved at
          // Session 58. The name says `po_issued` because what is new for this
          // reader is the DOCUMENT, which is what the link opens.
          'material_request_po_issued', 'inventory', 'requests_inbox', requestId
        );
      }
    } catch (e) {
      // Same reasoning as the requester's confirmation: the vendor has the
      // order, and an internal notification that did not send is a smaller
      // problem than a red error implying the order never happened.
      console.error('[#413] approver notification failed', e);
      notes.push('the approvers could not be notified in-app');
    }
  }

  const message = notes.length
    ? 'Order recorded — ' + notes.join('; ') + '.'
    : 'Order recorded. Nothing was sent, as you chose.';

  return { ok, message, mailtoHref };
}
